import { Box, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useQuery } from 'react-query';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRecoilValue } from 'recoil';
import { getDayOfWeek, getMonthText, getTypeWord } from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwCumulativeGraphByGender } from './RwwCumulativeGraphByGender';
import {
  KEY_CUMULATIVE,
  KEY_EDUCATION_OFFICE_MONTHLY_READ_STAT_LIST,
  KEY_EDUCATION_OFFICE_MONTHLY_WALK_STAT_LIST,
  KEY_EDUCATION_OFFICE_MONTHLY_WRITE_STAT_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';

import { getClassMonthlyReadRankList } from '../../common/api/ApiClassStat';
import {
  downloadEducationOfficeMonthlyDataList,
  getEducationOfficeMonthlyReadStatList,
  getEducationOfficeMonthlyWalkStatList,
  getEducationOfficeMonthlyWriteStatList,
} from '../../common/api/ApiEducationOffice';
import { educationOfficeInfoState } from '../../common/atom';

interface Props {
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwCumulativeStatistics: React.FC<Props> = ({
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const educationOfficeInfo = useRecoilValue(educationOfficeInfoState);
  const [educationOfficeSeq, setEducationOfficeSeq] = useState<number>(0);
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>();
  const [requestReadData, setRequestReadData] = useState<any>();
  const [requestWalkData, setRequestWalkData] = useState<any>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);
  const [statType, setStatType] = useState<string>(KEY_READ_TEXT);

  const {
    data,
    isLoading,
    refetch: refetchEducationOfficeMonthlyReadStat,
  } = useQuery(
    KEY_EDUCATION_OFFICE_MONTHLY_READ_STAT_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          educationOfficeSeq: educationOfficeSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getEducationOfficeMonthlyReadStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchEducationOfficeMonthlyWalkStat } = useQuery(
    KEY_EDUCATION_OFFICE_MONTHLY_WALK_STAT_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          educationOfficeSeq: educationOfficeSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getEducationOfficeMonthlyWalkStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchEducationOfficeMonthlyWriteStat } = useQuery(
    KEY_EDUCATION_OFFICE_MONTHLY_WRITE_STAT_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          educationOfficeSeq: educationOfficeSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getEducationOfficeMonthlyWriteStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const downloadEducationOfficeMonthlyData = () => {
    const newData: any = {
      type: getTypeWord(statType),
      dataType: KEY_CUMULATIVE,
      educationOfficeSeq: educationOfficeSeq,
      year: getYear(startDate * 1000),
      month: getMonthText(startDate * 1000),
    };
    downloadEducationOfficeMonthlyDataList(newData);
  };

  useEffect(() => {
    if (reqRequest) {
      refetchEducationOfficeMonthlyReadStat();
    }
  }, [educationOfficeSeq]);

  useEffect(() => {
    if (
      educationOfficeInfo.educationOfficeSeq !== undefined &&
      educationOfficeInfo.educationOfficeSeq !== null
    ) {
      setEducationOfficeSeq(educationOfficeInfo.educationOfficeSeq);
    }
  }, [educationOfficeInfo]);

  useEffect(() => {
    if (reqRequest) {
      refetchEducationOfficeMonthlyReadStat();
    }
  }, [startDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchEducationOfficeMonthlyReadStat();
    }
  }, [reqRequest]);

  useEffect(() => {
    setReqRequest(true);
  }, []);

  return (
    <>
      <Paper sx={{ backgroundColor: '#fff' }}>
        <SwTitleDownloadMore title='읽기/걷기/쓰기 누적 데이터' />
        <SwRwwTab
          onChangeTab={value => {
            setStatType(
              value === 0
                ? KEY_READ_TEXT
                : value === 1
                ? KEY_WALK_TEXT
                : KEY_WRITE_TEXT
            );
            value === 0
              ? refetchEducationOfficeMonthlyReadStat()
              : value === 1
              ? refetchEducationOfficeMonthlyWalkStat()
              : refetchEducationOfficeMonthlyWriteStat();
          }}
          onDownload={() => downloadEducationOfficeMonthlyData()}
        />
        <RwwCumulativeGraphByGender
          stepsGoalRateAverage={[false, false]}
          startDate={startDate}
          totalAverage={requestData?.totalRate ?? 0}
          data={requestData?.rate}
          isPdf={false}
        />
      </Paper>
    </>
  );
};
